import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Label,
  MessageBar,
  MessageBarBody,
  Option,
  useId,
} from '@fluentui/react-components';
import { InfoRegular } from '@fluentui/react-icons';
import { FolderWritePermissions } from '@mnspro-cloud/services';
import { find } from 'lodash';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../Button/Button';
import { Input } from '../Input';
import { containsIllegalChars } from './Files.utils';
import './FileNewFolderDialog.scss';

export interface FileNewFolderDialogProps {
  onConfirmClick: (folderName: string, writeAccess: FolderWritePermissions) => void;
  onCancelClick: () => void;
  consentRequired?: boolean;
  defaultValueWritePermissions?: FolderWritePermissions;
}

interface FolderWritePermissionDropdownProps {
  header: string;
  value: FolderWritePermissions;
}

export const FileNewFolderDialog: React.FC<FileNewFolderDialogProps> = ({
  onConfirmClick,
  onCancelClick,
  consentRequired,
  defaultValueWritePermissions = FolderWritePermissions.ownersOnly,
}) => {
  const { t } = useTranslation();
  const headerId = useId('fileNewFolderDialogHeader');
  const [folderName, setFolderName] = useState<string>('');
  const [writeAccess, setWriteAccess] = useState<FolderWritePermissions>(
    consentRequired ? FolderWritePermissions.none : defaultValueWritePermissions,
  );

  const writeAccessItems: FolderWritePermissionDropdownProps[] = useMemo(() => {
    const accessItems: FolderWritePermissionDropdownProps[] = [
      {
        header: t('file_new_folder_dialog_write_rights_owner', {
          defaultValue: 'Für Besitzer',
        }),
        value: FolderWritePermissions.ownersOnly,
      },
      {
        header: t('file_new_folder_dialog_write_rights_members', {
          defaultValue: 'Für Mitglieder',
        }),
        value: FolderWritePermissions.allUsers,
      },
    ];

    if (consentRequired) {
      accessItems.push({
        header: t('file_new_folder_dialog_write_rights_none', {
          defaultValue: 'Nicht unterstützt',
        }),
        value: FolderWritePermissions.none,
      });
    }

    return accessItems;
  }, [consentRequired, t]);

  const handleFolderNameChange = (name: string) => {
    setFolderName(name);
  };

  const trimmedFolderName = folderName.trim();
  const hasIllegalName = trimmedFolderName !== '' && containsIllegalChars(trimmedFolderName);

  const handleConfirmButtonClick = () => {
    onConfirmClick(trimmedFolderName, writeAccess);
  };

  const selectedItem = find(writeAccessItems, { value: writeAccess });
  const dropdownId = useId('writeAccessDropdown');
  const folderNameId = useId('folderNameInput');

  return (
    <Dialog open={true}>
      <DialogSurface className="fileNewFolderDialog" aria-labelledby={headerId}>
        <DialogBody>
          <DialogTitle id={headerId}>
            {t('file_new_folder_dialog_header', {
              defaultValue: 'Neuer Ordner',
            })}
          </DialogTitle>
          <DialogContent>
            <div className="flex flex-col items-stretch w-full">
              <div className="flex flex-col mb-2">
                <Label htmlFor={folderNameId}>
                  {t('file_new_folder_dialog_folder_name', {
                    defaultValue: 'Ordnername',
                  })}
                </Label>
                <Input
                  appearance="filled-darker"
                  id={folderNameId}
                  value={folderName}
                  onChange={(event) => handleFolderNameChange(event.target.value)}
                />
                {hasIllegalName && (
                  <MessageBar intent="error" className="alert">
                    <MessageBarBody>
                      {t('file_new_folder_dialog_illegal_name', {
                        defaultValue: 'Der Ordnername enthält ungültige Zeichen',
                      })}
                    </MessageBarBody>
                  </MessageBar>
                )}
              </div>
              <div className="flex flex-col mb-2">
                <Label htmlFor={dropdownId}>
                  {t('file_new_folder_dialog_write_rights', {
                    defaultValue: 'Schreibrechte',
                  })}
                </Label>
                <Dropdown
                  appearance="filled-darker"
                  id={dropdownId}
                  disabled={consentRequired}
                  value={selectedItem?.header ?? ''}
                  selectedOptions={selectedItem ? [selectedItem.value] : []}
                  onOptionSelect={(_event, data) => {
                    if (data.optionValue) {
                      setWriteAccess(data.optionValue as FolderWritePermissions);
                    }
                  }}
                >
                  {writeAccessItems.map((item) => (
                    <Option key={item.value} value={item.value}>
                      {item.header}
                    </Option>
                  ))}
                </Dropdown>
              </div>

              {consentRequired && (
                <MessageBar intent="info" className="consentMessage" icon={<InfoRegular />}>
                  <MessageBarBody>
                    {t('file_new_folder_dialog_consent_required_for_write_permissions', {
                      defaultValue:
                        'Das Auswählen spezieller Schreibrechte erfordert weitere App-Berechtigungen. Für weitere Unterstützung, wende dich bitte an den Administrator deiner Schule.',
                    })}
                  </MessageBarBody>
                </MessageBar>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={onCancelClick}>{t('cancel', { defaultValue: 'Abbrechen' })}</Button>
            <Button
              data-cy="fileCreateFolderButton"
              appearance="primary"
              disabled={trimmedFolderName === '' || hasIllegalName}
              onClick={handleConfirmButtonClick}
            >
              {t('file_new_folder_dialog_confirm_btn', {
                defaultValue: 'Ordner erstellen',
              })}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
